import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell } from "react-icons/fa";
import moment from "moment";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetFollowUpComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petFollowUps, setPetFollowUps] = useState([]);
    const [followUps, setFollowUps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingFollowUp, setEditingFollowUp] = useState(null);
    const [selectedFollowUp, setSelectedFollowUp] = useState("");
    const getTodayDateTimeLocal = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const local = new Date(now.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    const [recommendation, setRecommendation] = useState("");
    const [followUpDate, setFollowUpDate] = useState(getTodayDateTimeLocal());
    const [reminderDate, setReminderDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasFollowUpForAppointment, setHasFollowUpForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    useEffect(() => {
        fetchPetFollowUps();
    }, [appointmentId, petId]);

    const fetchPetFollowUps = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-follow-up/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const followUpsForPet = response.data.pet_follow_ups || [];
                setPetFollowUps(followUpsForPet);
                setFollowUps(response.data.follow_ups || []);
                setHasFollowUpForAppointment(
                    followUpsForPet.some(item =>
                        String(item.appointment_id) === String(appointmentId)
                        || String(item.appointment?.id) === String(appointmentId)
                        || String(item.appointment_id || item.appointment?.id || '') === String(appointmentId)
                    )
                );
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet follow-ups:", error);
            showErrorToast(t('failed to load follow ups'));
            setPetFollowUps([]);
            setFollowUps([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFollowUpSelect = (selected) => {
        const followUpId = selected?.value;
        setSelectedFollowUp(followUpId || "");
    };

    const handleAddFollowUp = () => {
        setEditingFollowUp(null);
        setSelectedFollowUp("");
        setRecommendation("");
        setFollowUpDate(getTodayDateTimeLocal());
        setReminderDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setShowModal(true);
    };

    const handleEditFollowUp = (followUp) => {
        setEditingFollowUp(followUp);
        setSelectedFollowUp(followUp.follow_up_id || followUp.followUp_id || (followUp.followUp?.id ?? ""));
        setRecommendation(followUp.recomendation || "");
        setFollowUpDate(followUp.date || "");
        setReminderDate(followUp.reminder_date || "");

        if (followUp.note && Array.isArray(followUp.note) && followUp.note.length > 0) {
            setGeneralNote(followUp.note[0].note || "");
            setExistingNoteId(followUp.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setShowModal(true);
    };

    const handleDeleteFollowUp = async (followUpId) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-follow-up-delete/${followUpId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetFollowUps();
                showSuccessToast(t('Follow up deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting follow up:", error);
            showErrorToast(t('failed to delete follow up'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (followUpId, followUpName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteFollowUp(followUpId);
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFollowUp) {
            showErrorToast(t('please select follow up'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingFollowUp
                ? `${process.env.REACT_APP_API_URL}/pet-follow-up-update/${editingFollowUp.id}`
                : `${process.env.REACT_APP_API_URL}/pet-follow-up`;

            const payload = {
                appointment_id: appointmentId,
                follow_up_id: selectedFollowUp ? parseInt(selectedFollowUp) : null,
                recomendation: recommendation || null,
                date: followUpDate || null,
                reminder_date: reminderDate || null,
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                showSuccessToast(editingFollowUp ? t('Follow up updated successfully!') : t('Follow up added successfully!'));
                fetchPetFollowUps();
            }
        } catch (error) {
            console.error("Error saving follow up:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_follow_up'));
        } finally {
            setSubmitting(false);
        }
    };

    const getFollowUpOptions = () => {
        return followUps.map(item => ({
            value: item.id,
            label: `${item.name}`,
        }));
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#e0e0e0' : 'white',
            color: '#333',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const groupFollowUpsByAppointment = () => {
        const grouped = {};
        petFollowUps.forEach(followUp => {
            const followUpAppointmentId = followUp.appointment_id;
            if (!grouped[followUpAppointmentId]) {
                grouped[followUpAppointmentId] = {
                    appointment: followUp.appointment,
                    followUps: []
                };
            }
            grouped[followUpAppointmentId].followUps.push(followUp);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading follow ups')}</p>
            </div>
        );
    }

    const followUpOptions = getFollowUpOptions();
    const selectedOption = followUpOptions.find(opt => opt.value === selectedFollowUp);
    const groupedFollowUps = groupFollowUpsByAppointment();

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('follow ups list')}</h6>
                {isAppointmentOpen && !hasFollowUpForAppointment && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddFollowUp}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={12} /> {t('add follow up')}
                    </Button>
                )}
            </div>

            {!petFollowUps || petFollowUps.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaStethoscope size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no follow ups found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="pet-followups-accordion"
                >
                    {Object.entries(groupedFollowUps).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, followUps: appointmentFollowUps } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.user.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentFollowUps.length} {t('follow-up(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-3">
                                    {/* Directly map followUps without nested accordion */}
                                    {appointmentFollowUps.map((followUp) => {
                                        if (!followUp) return null;
                                        const dateTime = followUp.date ? formatDateTime(followUp.date) : null;
                                        const noteText = followUp.note && Array.isArray(followUp.note) && followUp.note.length > 0
                                            ? followUp.note[0].note
                                            : null;

                                        return (
                                            <div key={followUp.id} className="card mb-3 border rounded">
                                                <div className="card-header bg-light py-3">
                                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <FaStethoscope className="text-primary" />
                                                            <Badge bg="info" className="px-3 py-2">
                                                                {followUp.followUp?.name || followUp.follow_up?.name || 'N/A'}
                                                            </Badge>

                                                            {followUp.date && (
                                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <FaCalendarAlt size={14} />
                                                                        {moment(followUp.date).format("DD MMM YYYY")}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <FaBell size={14} />
                                                                        {moment(followUp.date).format("hh:mm A")}
                                                                    </span>
                                                                </div>
                                                            )}

                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEditFollowUp(followUp)}
                                                                className="d-flex align-items-center gap-1"
                                                                style={{ background: '#3182ce', color: '#fff' }}
                                                            >
                                                                <FaEdit size={12} />
                                                                <span className="d-none d-sm-inline">{t('edit')}</span>
                                                            </Button>
                                                            {canDelete() && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteClick(followUp.id, followUp.followUp?.name || followUp.follow_up?.name || 'Follow Up')}
                                                                    className="d-flex align-items-center gap-1 bg-danger"
                                                                >
                                                                    <FaTrash size={12} />
                                                                    <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <Row className="g-4">
                                                        <Col md={12}>
                                                            <div className="p-3 bg-light rounded">
                                                                <div className="mb-2">
                                                                    <strong className="text-muted">{t('Follow Up Name')}:</strong>{' '}
                                                                    {followUp.followUp?.name || followUp.follow_up?.name || 'N/A'}
                                                                </div>
                                                                {followUp.followUp?.description && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Description')}:</strong>{' '}
                                                                        {followUp.followUp.description}
                                                                    </div>
                                                                )}
                                                                {followUp.date && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Follow Up Date')}:</strong>{' '}
                                                                        {moment(followUp.date).format("DD MMM YYYY hh:mm A")}
                                                                    </div>
                                                                )}
                                                                {followUp.reminder_date && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                        {moment(followUp.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                    </div>
                                                                )}
                                                                {noteText && (
                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <FaFileAlt className="text-success" />
                                                                            <strong className="text-muted">{t('note')}:</strong>
                                                                        </div>
                                                                        <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingFollowUp ? t('Edit Follow Up') : t('Add Follow Up')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Select Follow Up')} <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={followUpOptions}
                                value={selectedOption}
                                onChange={handleFollowUpSelect}
                                placeholder={t('select follow up')}
                                isSearchable
                                styles={customSelectStyles}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaCalendarAlt /> {t('Follow Up Date')}
                            </Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={followUpDate}
                                onChange={(e) => setFollowUpDate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaBell /> {t('Reminder Date')}
                            </Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={reminderDate}
                                onChange={(e) => setReminderDate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingFollowUp ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}